/* eslint-disable import/order */
import { Inject, Injectable } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { IpcMainEvent } from '@mona/events';
import { MonaRpcService } from './rpc.abstract.service';

/**
 * Communicates to the electron shell
 */
@Injectable({ providedIn: 'root' })
export class MonaRpcElectronService extends MonaRpcService {
    /**
     * Constructor
     *
     * @param window
     */
    constructor(@Inject(WINDOW) protected window: Window) {
        super(window);
    }

    /**
     * The legacy keys (mona-keys.json)
     */
    async getLegacyKeys() {
        return {
            version: 1,
            sshKey: {
                privateKey: 'hidden',
                publicKey: await window.MonaTerminal.crypto.getPublicKey('core'),
            },
            telemedicineSshKey: {
                privateKey: 'hidden',
                publicKey: await window.MonaTerminal.crypto.getPublicKey('telemedicine'),
            },
        };
    }

    /**
     * Calls node in an asynchronous
     *
     * @template T Expected result type
     * @param event The event handler registered at the main thread
     * @param payload The payload to send to the event handler
     */
    invoke<T>(event: IpcMainEvent, payload?: any): Promise<any> {
        console.warn('invoke', event, payload);

        switch (event) {
            // TODO: Use below Mona Terminal API without intermediate IPC events
            case IpcMainEvent.GET_JWT_TOKEN:
                return window.MonaTerminal.crypto.getJWT(payload.isTelemedicine ? 'telemedicine' : 'core');
            case IpcMainEvent.GET_MAC_ADDRESS:
                return window.MonaTerminal.getID();
            case IpcMainEvent.GET_KEYS:
                return this.getLegacyKeys();
            case IpcMainEvent.REBOOT:
                return window.MonaTerminal.settings.reboot();
            case IpcMainEvent.SHUTDOWN:
                return window.MonaTerminal.settings.shutdown();
            case IpcMainEvent.GET_CURRENT_TIMEZONE:
                return window.MonaTerminal.settings.getTimeZone();
            case IpcMainEvent.SET_TIME_ZONE:
                return window.MonaTerminal.settings.setTimeZone(payload);
            case IpcMainEvent.GET_CURRENT_BRIGHTNESS:
                return window.MonaTerminal.display.getBrightness();
            case IpcMainEvent.SET_CURRENT_BRIGHTNESS:
                return window.MonaTerminal.display.setBrightness(payload.value);
            case IpcMainEvent.RUN_PRE_CALL:
                return window.MonaTerminal.camera.enable();
            case IpcMainEvent.RUN_POST_CALL:
                return window.MonaTerminal.camera.disable();
            case IpcMainEvent.SET_OSK_AVAILABILITY:
                return window.MonaTerminal.osk.setAutoShow(payload);
            case IpcMainEvent.SET_OSK_THEME:
                return window.MonaTerminal.osk.setTheme(payload);
            case IpcMainEvent.SET_OSK_LANGUAGE:
                return window.MonaTerminal.osk.setLanguage(payload);
            case IpcMainEvent.BROWSER_NAVIGATE_BACK:
                return window.MonaTerminal.view.goBack();
            case IpcMainEvent.BROWSER_NAVIGATE_FORWARD:
                return window.MonaTerminal.view.goForward();
            case IpcMainEvent.BROWSER_NAVIGATE_HOME:
                return window.MonaTerminal.view.loadURL(payload);
            case IpcMainEvent.BROWSER_RELOAD:
                return window.MonaTerminal.view.reload();
            case IpcMainEvent.BROWSER_END_SESSION:
                return window.MonaTerminal.view.remove();
            case IpcMainEvent.BROWSER_VIEW_RESIZE:
                return window.MonaTerminal.view.setBounds(payload);
            case IpcMainEvent.BROWSER_SET_WINDOW_SHOWN:
                return window.MonaTerminal.view.setBounds(payload);
            case IpcMainEvent.BROWSER_SET_WINDOW_HIDDEN:
                return window.MonaTerminal.view.setBounds({ x: 0, y: 0, width: 0, height: 0 });

            // TODO: Remove below IPC events and related code
            case IpcMainEvent.GENERATE_TELEMEDICINE_DEVICE_KEYS:
            case IpcMainEvent.RESET_DHCP:
            case IpcMainEvent.GET_NETWORK_SETTINGS:
            case IpcMainEvent.GET_NETWORK_DETAILS:
            case IpcMainEvent.SCAN_WIFI:
            case IpcMainEvent.GET_WIFI_ENDPOINTS:
            case IpcMainEvent.CONNECT_WIFI:
            case IpcMainEvent.SET_MANUAL_CONNECTION:
            case IpcMainEvent.RELOAD:
            default:
                return Promise.resolve(null);
        }
    }
}
